/**
 * エラーメッセージ抽出ユーティリティ
 * APIエラーや一般的なエラーから適切なメッセージを抽出する
 */

import i18n from '../i18n/config';

interface ApiErrorResponse {
  response?: {
    data?: {
      error?: {
        message?: string;
      };
      message?: string;
    };
    status?: number;
  };
  message?: string;
  code?: string;
}

/**
 * エラーオブジェクトからメッセージを抽出（詳細版）
 * @param err エラーオブジェクト（unknown型）
 * @param defaultMessage デフォルトメッセージ
 * @returns 抽出されたエラーメッセージ
 */
export function extractErrorMessage(err: unknown, defaultMessage: string): string {
  // Axiosエラーの場合
  if (err && typeof err === 'object' && 'response' in err) {
    const apiError = err as ApiErrorResponse;
    const errorMessage = apiError.response?.data?.error?.message || apiError.response?.data?.message;
    
    if (errorMessage) {
      // バリデーションエラーの詳細を表示（フィールド名: メッセージの形式）
      // 例: "入力データが不正です: name: 予約表名称は必須です, startTime: 開始時刻の形式が不正です"
      return errorMessage;
    }
    
    // HTTPステータスコードに基づくメッセージ（現在の i18n 言語）
    const status = apiError.response?.status;
    if (status === 400 || status === 401 || status === 403 || status === 404 || status === 429 || status === 500) {
      return i18n.t(`common.error_http_${status}`);
    }
  }

  // Errorオブジェクトの場合
  if (err instanceof Error) {
    return err.message || defaultMessage;
  }
  
  // その他の場合
  if (err !== null && err !== undefined) {
    const errorString = String(err);
    if (errorString && errorString !== '{}' && errorString !== '[object Object]') {
      return errorString;
    }
  }
  
  return defaultMessage;
}

/** Axios 風の `response.status` を unknown から取り出す */
export function getErrorResponseStatus(err: unknown): number | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    const status = (err as { response?: { status?: unknown } }).response?.status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}

/** Axios / fetch 由来の `code`（例: ERR_BAD_REQUEST）を unknown から取り出す */
export function getErrorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/**
 * Axios の `response.data.error.message`、なければ `Error.message`。
 * TanStack Query / 手動 catch での API 失敗メッセージ取得に使う。
 */
export function getAxiosOrErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    const apiError = err as ApiErrorResponse;
    const fromBody = apiError.response?.data?.error?.message;
    if (typeof fromBody === 'string' && fromBody.length > 0) return fromBody;
  }
  if (err instanceof Error && err.message) return err.message;
  return undefined;
}

const DEFAULT_I18N_ERROR_PREFIXES = ['api.error.', 'sample.', 'usage_record.', 'storage_room.'] as const;

type TranslateFn = (key: string) => string;

/**
 * API が返す i18n キー（api.error.* / sample.* 等）を翻訳し、それ以外はそのまま表示する。
 */
export function translatePrefixedErrorMessage(
  message: string | undefined | null,
  t: TranslateFn,
  fallback: string,
  prefixes: readonly string[] = DEFAULT_I18N_ERROR_PREFIXES
): string {
  if (!message) return fallback;
  if (prefixes.some((prefix) => message.startsWith(prefix))) return t(message);
  return message;
}

/** getAxiosOrErrorMessage + translatePrefixedErrorMessage の合成 */
export function translateAxiosErrorMessage(
  err: unknown,
  t: TranslateFn,
  fallback: string,
  prefixes?: readonly string[]
): string {
  return translatePrefixedErrorMessage(getAxiosOrErrorMessage(err), t, fallback, prefixes);
}

/**
 * responseType: 'blob' の API 失敗時、JSON エラーボディを Blob から取り出してメッセージにする。
 */
export async function getAxiosOrBlobErrorMessage(
  err: unknown,
  fallback: string
): Promise<string> {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    // jsdom / Node 間で instanceof Blob が不安定なことがあるため duck-typing
    if (
      data &&
      typeof data === 'object' &&
      typeof (data as Blob).text === 'function'
    ) {
      try {
        const text = await (data as Blob).text();
        const json = JSON.parse(text) as {
          error?: { message?: string };
          message?: string;
        };
        const msg = json.error?.message || json.message;
        if (typeof msg === 'string' && msg.length > 0) return msg;
      } catch {
        /* ignore parse failures */
      }
    }
  }
  return getAxiosOrErrorMessage(err) || fallback;
}
